import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { 
  CheckCircle, 
  X, 
  Plus, 
  Minus, 
  Settings,
  Save,
  RefreshCw,
  AlertCircle,
  Package,
  Eye,
  EyeOff,
  Grid,
  Check,
  Square,
  CheckSquare
} from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Badge } from '../ui/Table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const FeatureMatrix = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [newFeature, setNewFeature] = useState('');
  const [showAddFeature, setShowAddFeature] = useState(false);
  const [changes, setChanges] = useState({});
  const [validationResults, setValidationResults] = useState({});
  const [constraints, setConstraints] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [bulkOperationMode, setBulkOperationMode] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [pendingBulkOperation, setPendingBulkOperation] = useState(null);

  const predefinedFeatures = [
    { key: 'ai_insights', name: 'AI Insights', description: 'AI-powered relationship insights and recommendations' },
    { key: 'gift_planning', name: 'Gift Planning', description: 'Gift reminder and planning tools' },
    { key: 'social_radar', name: 'Social Radar', description: 'Social media integration and monitoring' },
    { key: 'advanced_analytics', name: 'Advanced Analytics', description: 'Detailed analytics and reporting' },
    { key: 'priority_support', name: 'Priority Support', description: '24/7 priority customer support' },
    { key: 'white_label', name: 'White Label', description: 'Custom branding and white-label solutions' },
    { key: 'api_access', name: 'API Access', description: 'Full API access for integrations' },
    { key: 'custom_fields', name: 'Custom Fields', description: 'Custom contact fields and data' },
    { key: 'team_collaboration', name: 'Team Collaboration', description: 'Multi-user team collaboration features' },
    { key: 'data_export', name: 'Data Export', description: 'Export data in various formats' }
  ];

  useEffect(() => {
    fetchData();
    fetchConstraintsAndDependencies();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/plans`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
        
        // Extract unique features from all plans
        const allFeatures = new Set();
        const planFeatureMatrix = {};
        
        data.plans?.forEach(plan => {
          planFeatureMatrix[plan.id] = plan.current_version?.features || [];
          (plan.current_version?.features || []).forEach(feature => {
            allFeatures.add(feature);
          });
        });

        // Combine with predefined features
        const featureList = predefinedFeatures.map(f => ({
          ...f,
          isCustom: false
        }));

        // Add any custom features found in plans
        allFeatures.forEach(featureKey => {
          if (!predefinedFeatures.find(f => f.key === featureKey)) {
            featureList.push({
              key: featureKey,
              name: featureKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              description: 'Custom feature',
              isCustom: true
            });
          }
        });

        setFeatures(featureList);
        setMatrix(planFeatureMatrix);
        
        // Validate current matrix
        await validateCurrentMatrix(planFeatureMatrix);
      } else {
        setError('Failed to load plans and features');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load feature matrix information');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchConstraintsAndDependencies = async () => {
    try {
      const [constraintsResponse, dependenciesResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/monetization/features/constraints`, {
          credentials: 'include'
        }),
        fetch(`${BACKEND_URL}/api/admin/monetization/features/dependencies`, {
          credentials: 'include'
        })
      ]);

      if (constraintsResponse.ok) {
        const constraintsData = await constraintsResponse.json();
        setConstraints(constraintsData.constraints || []);
      }

      if (dependenciesResponse.ok) {
        const dependenciesData = await dependenciesResponse.json();
        setDependencies(dependenciesData.dependencies || []);
      }
    } catch (err) {
      console.error('Error fetching constraints and dependencies:', err);
    }
  };

  const validateCurrentMatrix = async (matrixData = matrix) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/features/validation`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setValidationResults(data.validation || {});
      }
    } catch (err) {
      console.error('Error validating matrix:', err);
    }
  };

  const handleFeatureToggle = async (planId, featureKey, enabled) => {
    const currentFeatures = matrix[planId] || [];
    let newFeatures;
    
    if (enabled) {
      newFeatures = [...currentFeatures, featureKey];
    } else {
      newFeatures = currentFeatures.filter(f => f !== featureKey);
    }

    // Update local state immediately for responsive UI
    const newMatrix = {
      ...matrix,
      [planId]: newFeatures
    };
    
    setMatrix(newMatrix);

    // Track changes
    setChanges(prev => ({
      ...prev,
      [planId]: newFeatures
    }));
    
    // Validate the updated matrix
    await validateCurrentMatrix(newMatrix);
  };

  const handleAddFeature = async () => {
    if (!newFeature.trim()) return;

    const featureKey = newFeature.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    if (features.find(f => f.key === featureKey)) {
      setError('Feature already exists');
      return;
    }

    const newFeatureObj = {
      key: featureKey,
      name: newFeature.trim(),
      description: 'Custom feature',
      isCustom: true
    };

    setFeatures(prev => [...prev, newFeatureObj]);
    setNewFeature('');
    setShowAddFeature(false);
  };

  const handleSaveChanges = async () => {
    if (Object.keys(changes).length === 0) {
      setError('No changes to save');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Create approval requests for each changed plan
      const promises = Object.entries(changes).map(async ([planId, features]) => {
        const plan = plans.find(p => p.id === planId);
        
        const payload = {
          action: 'update',
          plan_id: planId,
          changes: {
            features: features
          },
          justification: 'Updated feature matrix configuration',
          impact_assessment: {
            user_provided: `Feature changes for ${plan?.name}: ${features.length} features enabled`
          }
        };

        const response = await fetch(`${BACKEND_URL}/api/admin/monetization/plan-changes`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`Failed to save changes for ${plan?.name}`);
        }

        return response.json();
      });

      await Promise.all(promises);
      
      setChanges({});
      // Optionally refresh data
      fetchData();
    } catch (err) {
      console.error('Error saving changes:', err);
      setError('Failed to save feature matrix changes');
    } finally {
      setSaving(false);
    }
  };

  const isFeatureEnabled = (planId, featureKey) => {
    return (matrix[planId] || []).includes(featureKey);
  };

  const getFeatureIssues = (planId, featureKey) => {
    const allIssues = [
      ...(validationResults.errors || []),
      ...(validationResults.warnings || []),
      ...(validationResults.suggestions || [])
    ];
    
    return allIssues.filter(issue => 
      issue.plan_id === planId && 
      (issue.feature === featureKey || issue.dependent_feature_key === featureKey)
    );
  };

  const getPlanIssues = (planId) => {
    const allIssues = [
      ...(validationResults.errors || []),
      ...(validationResults.warnings || []),
      ...(validationResults.suggestions || [])
    ];
    
    return allIssues.filter(issue => issue.plan_id === planId);
  };

  const getIssueIcon = (severity) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
    }
  };

  const handleBulkToggle = () => {
    setBulkOperationMode(!bulkOperationMode);
    if (bulkOperationMode) {
      // Clear selections when exiting bulk mode
      setSelectedRows(new Set());
      setSelectedColumns(new Set());
    }
  };

  const handleRowSelection = (featureKey) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(featureKey)) {
      newSelectedRows.delete(featureKey);
    } else {
      newSelectedRows.add(featureKey);
    }
    setSelectedRows(newSelectedRows);
  };

  const handleColumnSelection = (planId) => {
    const newSelectedColumns = new Set(selectedColumns);
    if (newSelectedColumns.has(planId)) {
      newSelectedColumns.delete(planId);
    } else {
      newSelectedColumns.add(planId);
    }
    setSelectedColumns(newSelectedColumns);
  };

  const handleBulkOperation = (operation) => {
    const affectedCells = [];
    
    // If rows are selected, apply to all plans
    if (selectedRows.size > 0) {
      selectedRows.forEach(featureKey => {
        plans.forEach(plan => {
          affectedCells.push({ planId: plan.id, featureKey, planName: plan.name });
        });
      });
    }
    
    // If columns are selected, apply to all features
    if (selectedColumns.size > 0) {
      selectedColumns.forEach(planId => {
        const plan = plans.find(p => p.id === planId);
        features.forEach(feature => {
          affectedCells.push({ planId, featureKey: feature.key, planName: plan?.name });
        });
      });
    }
    
    // If both rows and columns are selected, apply to intersection
    if (selectedRows.size > 0 && selectedColumns.size > 0) {
      const intersectionCells = [];
      selectedRows.forEach(featureKey => {
        selectedColumns.forEach(planId => {
          const plan = plans.find(p => p.id === planId);
          intersectionCells.push({ planId, featureKey, planName: plan?.name });
        });
      });
      affectedCells.length = 0;
      affectedCells.push(...intersectionCells);
    }
    
    setPendingBulkOperation({ operation, affectedCells });
    setShowBulkConfirm(true);
  };

  const confirmBulkOperation = async () => {
    if (!pendingBulkOperation) return;
    
    const { operation, affectedCells } = pendingBulkOperation;
    const newMatrix = { ...matrix };
    const newChanges = { ...changes };
    
    affectedCells.forEach(({ planId, featureKey }) => {
      const currentFeatures = newMatrix[planId] || [];
      let updatedFeatures;
      
      if (operation === 'enable') {
        updatedFeatures = [...new Set([...currentFeatures, featureKey])];
      } else if (operation === 'disable') {
        updatedFeatures = currentFeatures.filter(f => f !== featureKey);
      } else if (operation === 'toggle') {
        if (currentFeatures.includes(featureKey)) {
          updatedFeatures = currentFeatures.filter(f => f !== featureKey);
        } else {
          updatedFeatures = [...currentFeatures, featureKey];
        }
      }
      
      newMatrix[planId] = updatedFeatures;
      newChanges[planId] = updatedFeatures;
    });
    
    setMatrix(newMatrix);
    setChanges(newChanges);
    
    // Validate the updated matrix
    await validateCurrentMatrix(newMatrix);
    
    // Clear bulk operation state
    setShowBulkConfirm(false);
    setPendingBulkOperation(null);
    setBulkOperationMode(false);
    setSelectedRows(new Set());
    setSelectedColumns(new Set());
  };

  const cancelBulkOperation = () => {
    setShowBulkConfirm(false);
    setPendingBulkOperation(null);
  };

  const hasChanges = Object.keys(changes).length > 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading feature matrix...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Feature Matrix</h3>
          <p className="text-gray-600 dark:text-gray-300">Manage features across all subscription plans</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowAddFeature(!showAddFeature)} 
            variant="outline" 
            size="sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Feature
          </Button>
          <Button 
            onClick={handleBulkToggle} 
            variant={bulkOperationMode ? "solid" : "outline"} 
            size="sm"
          >
            <Grid className="w-4 h-4 mr-2" />
            {bulkOperationMode ? 'Exit Bulk' : 'Bulk Operations'}
          </Button>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {hasChanges && (
            <Button onClick={handleSaveChanges} size="sm" disabled={saving}>
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Validation Results */}
      {validationResults.errors && validationResults.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center mb-2">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200 font-medium">
              {validationResults.errors.length} Constraint Violation{validationResults.errors.length > 1 ? 's' : ''}
            </p>
          </div>
          <ul className="space-y-1">
            {validationResults.errors.map((error, index) => (
              <li key={index} className="text-sm text-red-700 dark:text-red-300">
                • {error.message} - {error.action}
              </li>
            ))}
          </ul>
        </div>
      )}

      {validationResults.warnings && validationResults.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/10 dark:border-yellow-800">
          <div className="flex items-center mb-2">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
            <p className="text-yellow-800 dark:text-yellow-200 font-medium">
              {validationResults.warnings.length} Warning{validationResults.warnings.length > 1 ? 's' : ''}
            </p>
          </div>
          <ul className="space-y-1">
            {validationResults.warnings.map((warning, index) => (
              <li key={index} className="text-sm text-yellow-700 dark:text-yellow-300">
                • {warning.message} - {warning.action}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/10 dark:border-yellow-800">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
            <div>
              <p className="text-yellow-800 dark:text-yellow-200 font-medium">Unsaved Changes</p>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                You have unsaved changes to {Object.keys(changes).length} plan(s). These changes will require approval.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Operations Controls */}
      {bulkOperationMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/10 dark:border-blue-800">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-blue-800 dark:text-blue-200 font-medium">Bulk Operations Mode</p>
              <p className="text-blue-700 dark:text-blue-300 text-sm">
                Select rows (features) or columns (plans) then apply bulk operations
              </p>
            </div>
            <div className="flex gap-2">
              {(selectedRows.size > 0 || selectedColumns.size > 0) && (
                <>
                  <Button onClick={() => handleBulkOperation('enable')} size="sm" variant="outline">
                    <Check className="w-4 h-4 mr-2" />
                    Enable Selected
                  </Button>
                  <Button onClick={() => handleBulkOperation('disable')} size="sm" variant="outline">
                    <X className="w-4 h-4 mr-2" />
                    Disable Selected
                  </Button>
                  <Button onClick={() => handleBulkOperation('toggle')} size="sm" variant="outline">
                    <Settings className="w-4 h-4 mr-2" />
                    Toggle Selected
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="text-sm text-blue-600 dark:text-blue-400">
            Selected: {selectedRows.size} feature{selectedRows.size !== 1 ? 's' : ''}, {selectedColumns.size} plan{selectedColumns.size !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Add Feature Form */}
      {showAddFeature && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              placeholder="Enter feature name..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
              onKeyPress={(e) => e.key === 'Enter' && handleAddFeature()}
            />
            <Button onClick={handleAddFeature} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
            <Button onClick={() => setShowAddFeature(false)} variant="outline" size="sm">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Feature Matrix Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[300px]">
                <div className="flex items-center gap-2">
                  {bulkOperationMode && (
                    <div className="w-6 h-6 flex items-center justify-center">
                      <Square className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  Feature
                </div>
              </TableHead>
              {plans.map(plan => (
                <TableHead key={plan.id} className="text-center min-w-[120px]">
                  <div>
                    <div className="flex items-center justify-center gap-2 mb-1">
                      {bulkOperationMode && (
                        <button
                          onClick={() => handleColumnSelection(plan.id)}
                          className={`p-1 rounded ${
                            selectedColumns.has(plan.id) 
                              ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/20' 
                              : 'text-gray-400 hover:text-blue-600'
                          }`}
                        >
                          {selectedColumns.has(plan.id) 
                            ? <CheckSquare className="w-4 h-4" /> 
                            : <Square className="w-4 h-4" />
                          }
                        </button>
                      )}
                      <div>
                        <div className="font-medium">{plan.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{plan.key}</div>
                      </div>
                    </div>
                    {plan.is_most_popular && (
                      <Badge variant="purple" className="mt-1 text-xs">Popular</Badge>
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {features.map(feature => (
              <TableRow key={feature.key}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {bulkOperationMode && (
                      <button
                        onClick={() => handleRowSelection(feature.key)}
                        className={`p-1 rounded ${
                          selectedRows.has(feature.key) 
                            ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/20' 
                            : 'text-gray-400 hover:text-blue-600'
                        }`}
                      >
                        {selectedRows.has(feature.key) 
                          ? <CheckSquare className="w-4 h-4" /> 
                          : <Square className="w-4 h-4" />
                        }
                      </button>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {feature.name}
                        </div>
                        {feature.isCustom && (
                          <Badge variant="info" className="text-xs">Custom</Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {feature.description}
                      </div>
                    </div>
                  </div>
                </TableCell>
                {plans.map(plan => {
                  const enabled = isFeatureEnabled(plan.id, feature.key);
                  const hasChange = changes[plan.id] && 
                    JSON.stringify(changes[plan.id]) !== JSON.stringify(plan.current_version?.features || []);
                  const issues = getFeatureIssues(plan.id, feature.key);
                  const hasErrors = issues.some(issue => issue.severity === 'error');
                  const hasWarnings = issues.some(issue => issue.severity === 'warning');
                  
                  return (
                    <TableCell key={`${plan.id}-${feature.key}`} className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleFeatureToggle(plan.id, feature.key, !enabled)}
                          className={`p-2 rounded-full border-2 transition-colors ${
                            enabled 
                              ? 'bg-green-100 border-green-500 text-green-600 hover:bg-green-200 dark:bg-green-900/20 dark:border-green-400 dark:text-green-400'
                              : 'bg-gray-100 border-gray-300 text-gray-400 hover:bg-gray-200 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-500'
                          } ${hasChange ? 'ring-2 ring-yellow-400' : ''} ${hasErrors ? 'ring-2 ring-red-500' : ''}`}
                          title={issues.length > 0 ? issues.map(i => i.message).join('. ') : ''}
                        >
                          {enabled ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        </button>
                        
                        {/* Validation indicators */}
                        {hasErrors && (
                          <div className="w-2 h-2 bg-red-500 rounded-full" title="Constraint violation" />
                        )}
                        {hasWarnings && !hasErrors && (
                          <div className="w-2 h-2 bg-yellow-500 rounded-full" title="Warning" />
                        )}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Feature Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map(plan => {
          const enabledFeatures = matrix[plan.id] || [];
          return (
            <div key={plan.id} className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <Package className="w-5 h-5 text-indigo-600" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">{plan.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{plan.key}</div>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                {enabledFeatures.length}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                features enabled
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Bulk Operation Confirmation Modal */}
      {showBulkConfirm && pendingBulkOperation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Confirm Bulk Operation
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Are you sure you want to <strong>{pendingBulkOperation.operation}</strong> features for{' '}
              <strong>{pendingBulkOperation.affectedCells.length}</strong> cell{pendingBulkOperation.affectedCells.length !== 1 ? 's' : ''}?
            </p>
            
            {/* Preview of affected cells */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 mb-4 max-h-40 overflow-y-auto">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Affected cells:
              </div>
              <div className="space-y-1">
                {pendingBulkOperation.affectedCells.slice(0, 10).map((cell, index) => {
                  const feature = features.find(f => f.key === cell.featureKey);
                  return (
                    <div key={index} className="text-xs text-gray-600 dark:text-gray-400">
                      {feature?.name} → {cell.planName}
                    </div>
                  );
                })}
                {pendingBulkOperation.affectedCells.length > 10 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    ...and {pendingBulkOperation.affectedCells.length - 10} more
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button onClick={cancelBulkOperation} variant="outline">
                Cancel
              </Button>
              <Button onClick={confirmBulkOperation} className="bg-blue-600 hover:bg-blue-700">
                Confirm {pendingBulkOperation.operation}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureMatrix;